import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTab } from "../../../contexts/tab";
import { basePath } from "../../config";
import { toast } from "react-toastify";
import { useAuth } from "../../../contexts/auth";
import { useFormik } from "formik";
import classNames from "classnames";
import * as Yup from "yup";

const submitFormData = async (
  values,
  actions,
  paperColorData,
  paperColorToEdit,
  isEditMode,
  activeUser,
  setTabData,
  tabData,
  setPaperColorData,
  setPaperColorToEdit,
  setIsOpen
) => {
  // TODO:: add form validation
  try {
    const requestBody = { ...values, paper_id: paperColorData.id };

    if (isEditMode) {
      requestBody.id = paperColorToEdit.id;
    }

    const res = await fetch(
      basePath + (isEditMode ? "editPaperColor" : "addPaperColor"),
      {
        method: "post",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${activeUser?.remember_token}`,
        },
        body: JSON.stringify(requestBody, null, 2),
      }
    );

    const data = await res.json();

    if (res.status === 401) {
      toast.error(data.message);
      actions.resetForm({ values: values });
      document.cookie = "user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      return 0;
    }

    if (data.status) {
      console.log(JSON.parse(data.data));
      const paperTypes = JSON.parse(data.data);
      setTabData({ ...paperTypes, links: tabData.links });
      setPaperColorData({
        ...paperColorData,
        color: paperTypes.data.find(({ id }) => id === paperColorData.id).color,
      });
      setIsOpen(false);
      actions.resetForm({ color: "", note: "" });
      setPaperColorToEdit(null);
      toast.success(data.message);
    } else {
      // actions.resetForm({ values: values });
      toast.error("error: " + data.message);
      console.error("error: " + data.message);
    }
  } catch (error) {
    actions.resetForm({ values: values });
    toast.error("error: " + error);
    console.error("TabProvider:: api calling fail =>", error);
  }
};

const validationSchema = Yup.object().shape({
  color: Yup.string().required(),
});

export default function PaperColorForm({
  isOpen,
  setIsOpen,
  paperColorToEdit,
  setPaperColorToEdit,
  setPaperColorData,
  paperColorData,
}) {
  const [{ tabData }, { setTabData }] = useTab();
  const [{ activeUser }] = useAuth();

  const isEditMode = !!paperColorToEdit;

  useEffect(() => {
    if (paperColorToEdit) {
      formik.setFieldValue("color", paperColorToEdit?.color);
      formik.setFieldValue("note", paperColorToEdit?.note || "");
      setIsOpen(true);
    }
  }, [paperColorToEdit]);

  function closeModal() {
    setIsOpen(false);
    formik.resetForm({ color: "", note: "" });
    setPaperColorToEdit(null);
  }

  const formik = useFormik({
    initialValues: { color: "", note: "" },
    validationSchema,
    onSubmit: (values, actions) => {
      submitFormData(
        values,
        actions,
        paperColorData,
        paperColorToEdit,
        isEditMode,
        activeUser,
        setTabData,
        tabData,
        setPaperColorData,
        setPaperColorToEdit,
        setIsOpen
      );
    },
  });

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  {isEditMode ? "Edit" : " Add"} Paper Color
                </Dialog.Title>
                <form
                  className="mt-2 grid gap-4"
                  onSubmit={formik.handleSubmit}
                >
                  <div>
                    <label className="block">
                      <span className="block text-sm font-medium text-slate-700 capitalize">
                        color
                      </span>
                      <input
                        type="text"
                        name="color"
                        value={formik.values.color || ""}
                        onChange={formik.handleChange}
                        className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md text-sm shadow-sm placeholder-slate-400"
                      />
                    </label>
                    {formik.errors.color && formik.touched.color && (
                      <div className="text-red-500 text-sm">
                        {formik.errors.color}
                      </div>
                    )}
                  </div>

                  <label className="block">
                    <span className="block text-sm font-medium text-slate-700 capitalize">
                      notes
                    </span>
                    <textarea
                      name="note"
                      value={formik.values.note || ""}
                      onChange={formik.handleChange}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md text-sm shadow-sm placeholder-slate-400"
                      rows="5"
                    ></textarea>
                  </label>
                  <div className="mt-4 flex justify-end gap-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={formik.isSubmitting}
                      className={classNames(
                        "inline-flex justify-center rounded-md border border-transparent bg-green-100 px-4 py-2 text-sm font-medium text-green-900 hover:bg-green-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2",
                        formik.isSubmitting && "disabled:bg-slate-300"
                      )}
                    >
                      {isEditMode ? "Edit" : " Add"} Paper Color
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
